import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ComercioIA, IaService, RespuestaAsistente, TurnoHistorial } from 'src/app/services/ia/ia.service';
import { errorDeIA } from 'src/app/services/ia/ia-formato';

/**
 * SOLAPA «PROBAR» — `console/ia/probar`. La única entrada al asistente desde la
 * consola en esta vuelta (CONTRATO §8.2 y §10).
 *
 * Se elige un comercio y se pregunta COMO SU ADMINISTRADOR: `POST /admin/probar`
 * corre aunque el comercio tenga el asistente apagado —para probar antes de
 * prenderlo—, respeta el interruptor general, la clave, el agente y el tope
 * diario, y queda anotado en la bitácora como «Prueba».
 *
 * 📍 El hilo se manda como `historial` para poder repreguntar: hasta 12 mensajes
 * (6 idas y vueltas), 2.000 caracteres cada uno (CONTRATO §11.5, igual que el
 * portal; el núcleo recorta igual). Cambiar de comercio
 * empieza un hilo nuevo: la conversación de un comercio no se le mezcla a otro.
 *
 * 📍 `?comercio=MT` llega desde la ficha del comercio («Probar como este comercio»).
 */
@Component({
  selector: 'app-ia-probar',
  templateUrl: './ia-probar.component.html',
  styleUrls: ['./ia-probar.component.css']
})
export class IaProbarComponent implements OnInit {

  readonly ARRANQUE = [
    '¿Cuánto vendí esta semana por tienda?',
    '¿Qué me falta cargar en mi ficha?',
    '¿Cómo funciona la liquidación?',
  ];

  comercios: ComercioIA[] = [];
  cargandoComercios = true;
  errorComercios: string | null = null;

  comercio = '';
  pregunta = '';
  preguntando = false;

  hilo: { pregunta: string; respuesta: RespuestaAsistente | null; error: string | null }[] = [];

  constructor(private _ia: IaService, private ruta: ActivatedRoute, private router: Router) { }

  ngOnInit(): void {
    this.comercio = (this.ruta.snapshot.queryParamMap.get('comercio') || '').toUpperCase();
    this.cargarComercios();
  }

  cargarComercios = async (): Promise<void> => {
    this.cargandoComercios = true;
    this.errorComercios = null;
    try {
      const r = await this._ia.getComercios();
      this.comercios = (Array.isArray(r) ? r : [])
        .slice().sort((a, b) => (a.nombre || a.codigo).localeCompare(b.nombre || b.codigo));
    } catch (e: any) {
      this.comercios = [];
      this.errorComercios = errorDeIA(e);
    } finally {
      this.cargandoComercios = false;
    }
  };

  get elegido(): ComercioIA | null { return this.comercios.find((c) => c.codigo === this.comercio) || null; }

  get enLista(): boolean { return !!this.elegido; }

  cambiarComercio = (codigo: string): void => {
    this.comercio = (codigo || '').toUpperCase();
    this.hilo = [];
    this.router.navigate([], { relativeTo: this.ruta, replaceUrl: true, queryParams: { comercio: this.comercio || null } });
  };

  /** Los últimos 12 mensajes del hilo (6 idas y vueltas), 2.000 caracteres cada uno (§11.5). */
  private historial = (): TurnoHistorial[] => {
    const turnos: TurnoHistorial[] = [];
    for (const t of this.hilo) {
      turnos.push({ rol: 'usuario', texto: t.pregunta.slice(0, 2000) });
      if (t.respuesta?.respuesta) turnos.push({ rol: 'asistente', texto: t.respuesta.respuesta.slice(0, 2000) });
    }
    return turnos.slice(-12);
  };

  preguntar = async (texto?: string): Promise<void> => {
    const q = (texto ?? this.pregunta).trim();
    if (!q || !this.comercio || this.preguntando) return;
    if (q.length > 1000) return;
    const historial = this.historial();
    const turno = { pregunta: q, respuesta: null as RespuestaAsistente | null, error: null as string | null };
    this.hilo.push(turno);
    this.pregunta = '';
    this.preguntando = true;
    try {
      turno.respuesta = await this._ia.probar(this.comercio.trim().toUpperCase(), q, historial);
    } catch (e: any) {
      turno.error = errorDeIA(e);
    } finally {
      this.preguntando = false;
    }
  };

  alTeclear = (ev: KeyboardEvent): void => {
    if (ev.key === 'Enter' && (ev.metaKey || ev.ctrlKey)) { ev.preventDefault(); this.preguntar(); }
  };

  empezarDeNuevo = (): void => { this.hilo = []; this.pregunta = ''; };
}
